import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { doc, limit, onSnapshot, orderBy } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { useCouple } from "../hooks/useCouple";
import { useCoupleCollection } from "../hooks/useCoupleCollection";
import { useLocationSharing } from "../hooks/useLocationSharing";
import { ALL_NAV_ITEMS, MAX_ITEMS as MAX_NAV_ITEMS, MIN_ITEMS as MIN_NAV_ITEMS, setBottomNavIds, useBottomNavIds } from "../lib/bottomNavItems";
import { Sheet } from "../components/Sheet";
import { nextOccurrenceOnOrAfter, occursOnDate, toISODate } from "../lib/recurrence";
import type { CalendarEvent, EventCategory, ImportantDate, ImportantDateCategory, LinkBoard, Mood, Note, SavingsGoal, Todo, WirList } from "../types";

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function nextOccurrence(dateStr: string, repeatsYearly: boolean): Date {
  const base = new Date(dateStr + "T00:00");
  if (!repeatsYearly) return base;
  const now = new Date();
  const next = new Date(now.getFullYear(), base.getMonth(), base.getDate());
  if (next < new Date(now.getFullYear(), now.getMonth(), now.getDate())) next.setFullYear(now.getFullYear() + 1);
  return next;
}

function daysUntil(d: Date): number {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}

function daysSince(dateStr: string): number {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const start = new Date(dateStr + "T00:00");
  return Math.max(0, Math.round((today.getTime() - start.getTime()) / 86400000));
}

function dateBadge(d: ImportantDate): string {
  if (d.kind === "countup") return `seit ${daysSince(d.date)} Tagen`;
  const days = daysUntil(nextOccurrence(d.date, d.repeatsYearly));
  return days === 0 ? "Heute" : days === 1 ? "Morgen" : `in ${days} Tagen`;
}

const WIDGETS = [
  { id: "mood", label: "Stimmung (oben)" },
  { id: "calendar", label: "Nächste Termine" },
  { id: "todos", label: "Offene Todos" },
  { id: "savings", label: "Sparziele" },
  { id: "shopping", label: "Listen" },
  { id: "dates", label: "Wichtige Daten" },
  { id: "notes", label: "Letzte Notiz" },
  { id: "links", label: "Links" },
] as const;
type WidgetId = (typeof WIDGETS)[number]["id"];
const STORAGE_KEY = "wir_home_widgets";
// "links" was added after this storage key already existed for some users —
// this one-time migration turns it on for them too (as if it had always
// been there), instead of it silently defaulting to off just because it
// wasn't around yet to be saved. Guarded by its own flag so a later,
// deliberate toggle-off of "links" isn't undone on the next load.
const LINKS_MIGRATED_KEY = "wir_home_widgets_migrated_links";

function loadEnabled(): Set<WidgetId> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set(WIDGETS.map((w) => w.id));
    const arr = JSON.parse(raw) as string[];
    const set = new Set(arr.filter((id) => WIDGETS.some((w) => w.id === id)) as WidgetId[]);
    if (!localStorage.getItem(LINKS_MIGRATED_KEY)) {
      set.add("links");
      localStorage.setItem(LINKS_MIGRATED_KEY, "1");
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
    }
    return set;
  } catch {
    return new Set(WIDGETS.map((w) => w.id));
  }
}

function formatDistanceKm(km: number): string {
  return km < 1 ? "< 1 km entfernt" : `${Math.round(km)} km entfernt`;
}

export function Home() {
  const { user } = useAuth();
  const { profile, partner, couple } = useCouple();
  const { distanceKm, enabled: locationEnabled, busy: locationBusy, refresh: refreshLocation } = useLocationSharing();
  const [enabled, setEnabled] = useState<Set<WidgetId>>(() => loadEnabled());
  const [customizeOpen, setCustomizeOpen] = useState(false);

  function toggle(id: WidgetId) {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
      return next;
    });
  }

  // Bottom-nav selection: order is the selection order itself (newly ticked
  // items append to the end), capped at MAX_NAV_ITEMS and floored at
  // MIN_NAV_ITEMS so the bar can never end up empty or with just one tab.
  const navIds = useBottomNavIds();
  function toggleNavItem(id: string) {
    if (navIds.includes(id)) {
      if (navIds.length <= MIN_NAV_ITEMS) return;
      setBottomNavIds(navIds.filter((i) => i !== id));
    } else {
      if (navIds.length >= MAX_NAV_ITEMS) return;
      setBottomNavIds([...navIds, id]);
    }
  }

  // Fetched unfiltered (bounded by `limit`) rather than queried by date,
  // because a recurring event's own startDate/endDate can be in the past
  // even though it still occurs again soon — nextOccurrenceOnOrAfter
  // accounts for that, which a Firestore range query on stored fields can't.
  const { items: allEvents } = useCoupleCollection<CalendarEvent>(
    couple?.id ?? null,
    "calendarEvents",
    orderBy("startDate", "asc"),
    limit(100)
  );
  const { items: eventCategories } = useCoupleCollection<EventCategory>(couple?.id ?? null, "eventCategories", orderBy("createdAt", "asc"));
  const categoryById = useMemo(() => new Map(eventCategories.map((c) => [c.id, c])), [eventCategories]);
  const todayStr = todayISO();
  const tomorrowStr = useMemo(() => {
    const d = new Date(todayStr + "T00:00");
    d.setDate(d.getDate() + 1);
    return toISODate(d);
  }, [todayStr]);
  const todayEvents = useMemo(
    () =>
      allEvents
        .filter((ev) => occursOnDate(ev, todayStr))
        .slice()
        .sort((a, b) => (a.time ?? "").localeCompare(b.time ?? "")),
    [allEvents, todayStr]
  );
  // Anything on or after today, excluding today itself — split into its own
  // "Morgen" group (so a tomorrow event doesn't get buried as just another
  // "Demnächst" line) and the rest, which stays unlabeled further out.
  const upcoming = useMemo(
    () =>
      allEvents
        .map((ev) => ({ ev, next: nextOccurrenceOnOrAfter(ev, todayStr) }))
        .filter((x): x is { ev: CalendarEvent; next: string } => x.next !== null && x.next !== todayStr)
        .sort((a, b) => a.next.localeCompare(b.next)),
    [allEvents, todayStr]
  );
  const tomorrowEvents = useMemo(() => upcoming.filter((x) => x.next === tomorrowStr), [upcoming, tomorrowStr]);
  const laterEvents = useMemo(() => upcoming.filter((x) => x.next !== tomorrowStr).slice(0, 3), [upcoming, tomorrowStr]);
  const { items: todos } = useCoupleCollection<Todo>(couple?.id ?? null, "todos", orderBy("createdAt", "asc"));
  const openTodos = todos.filter((t) => !t.done);
  const { items: goals } = useCoupleCollection<SavingsGoal>(couple?.id ?? null, "savingsGoals", orderBy("createdAt", "asc"), limit(2));
  const topGoal = goals[0];
  const topGoalPct = topGoal && topGoal.targetAmountCents > 0 ? Math.min(100, Math.round((topGoal.currentAmountCents / topGoal.targetAmountCents) * 100)) : null;
  const { items: lists } = useCoupleCollection<WirList>(couple?.id ?? null, "lists", orderBy("createdAt", "asc"));
  const { items: linkBoards } = useCoupleCollection<LinkBoard>(couple?.id ?? null, "linkBoards", orderBy("createdAt", "asc"));
  const { items: importantDates } = useCoupleCollection<ImportantDate>(couple?.id ?? null, "importantDates", orderBy("date", "asc"));
  const { items: dateCategories } = useCoupleCollection<ImportantDateCategory>(couple?.id ?? null, "importantDateCategories", orderBy("createdAt", "asc"));
  const dateCategoryById = useMemo(() => new Map(dateCategories.map((c) => [c.id, c])), [dateCategories]);
  // Nothing pinned yet (showOnHome never set) is the common case right after
  // this feature ships, so the card still shows something useful instead of
  // going blank: the single nearest countdown, same as the old behavior.
  const pinnedDates = useMemo(
    () => importantDates.filter((d) => d.showOnHome && (d.kind === "countup" || d.repeatsYearly || daysUntil(nextOccurrence(d.date, d.repeatsYearly)) >= 0)),
    [importantDates]
  );
  const nextDate = useMemo(() => {
    // Excludes a non-repeating date whose day has already passed — same
    // "ausgeblendet" treatment as the Wichtige Daten page itself, so an
    // expired one-off doesn't win this sort just for having the oldest
    // "next occurrence" and show up here as a bogus negative countdown.
    const countdowns = importantDates.filter(
      (d) => d.kind !== "countup" && (d.repeatsYearly || daysUntil(nextOccurrence(d.date, d.repeatsYearly)) >= 0)
    );
    if (countdowns.length === 0) return null;
    return [...countdowns].sort(
      (a, b) => nextOccurrence(a.date, a.repeatsYearly).getTime() - nextOccurrence(b.date, b.repeatsYearly).getTime()
    )[0];
  }, [importantDates]);
  const { items: notes } = useCoupleCollection<Note>(couple?.id ?? null, "notes", orderBy("updatedAt", "desc"), limit(1));

  const [myMood, setMyMood] = useState<Mood | null>(null);
  const [partnerMood, setPartnerMood] = useState<Mood | null>(null);
  useEffect(() => {
    if (!couple || !user) return;
    return onSnapshot(doc(db, "couples", couple.id, "moods", user.uid), (s) => setMyMood(s.exists() ? (s.data() as Mood) : null));
  }, [couple, user]);
  useEffect(() => {
    if (!couple || !partner) {
      setPartnerMood(null);
      return;
    }
    return onSnapshot(doc(db, "couples", couple.id, "moods", partner.uid), (s) => setPartnerMood(s.exists() ? (s.data() as Mood) : null));
  }, [couple, partner]);
  const isToday = (m: Mood | null) => !!m && m.date === todayISO();

  return (
    <main className="min-h-dvh pb-28">
      <div className="hero-glow px-5 pb-6 pt-7">
        <div className="mx-auto max-w-md">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-plum-100/70">Schön, dass ihr da seid</p>
              <h1 className="mt-1 font-display text-2xl font-semibold text-cream-50">
                {profile?.displayName}
                {partner ? ` & ${partner.displayName}` : ""}
              </h1>
              {(couple?.togetherSince || distanceKm !== null) && (
                <p className="mt-0.5 flex items-center gap-1 text-xs text-plum-100/60">
                  {couple?.togetherSince && `zusammen seit ${daysSince(couple.togetherSince)} Tagen`}
                  {couple?.togetherSince && distanceKm !== null && " · "}
                  {distanceKm !== null && (
                    locationEnabled ? (
                      <button
                        type="button"
                        onClick={() => refreshLocation()}
                        disabled={locationBusy}
                        className="inline-flex items-center gap-1 disabled:opacity-60"
                        aria-label="Entfernung aktualisieren"
                      >
                        {formatDistanceKm(distanceKm)}
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className={`h-3 w-3 flex-shrink-0 ${locationBusy ? "animate-spin" : ""}`}
                        >
                          <path d="M20 11a8 8 0 1 0-2.3 5.7M20 5v6h-6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    ) : (
                      formatDistanceKm(distanceKm)
                    )
                  )}
                </p>
              )}
            </div>
            <button
              onClick={() => setCustomizeOpen(true)}
              aria-label="Startseite anpassen"
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white/10 text-cream-50 ring-1 ring-white/20"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-4.5 w-4.5">
                <path d="M4 7h10M4 12h16M4 17h7" strokeLinecap="round" />
                <circle cx="17" cy="7" r="2" />
                <circle cx="9" cy="17" r="2" />
              </svg>
            </button>
          </div>

          {enabled.has("mood") && (
            <Link to="/mood" className="mt-6 flex items-center gap-4">
              <MoodHeroChip label={profile?.displayName ?? "Du"} mood={isToday(myMood) ? myMood : null} />
              <span className="h-9 w-px flex-shrink-0 bg-white/15" />
              <MoodHeroChip label={partner?.displayName ?? "Partner"} mood={isToday(partnerMood) ? partnerMood : null} />
            </Link>
          )}
        </div>
      </div>

      <div className="mx-auto mt-5 max-w-md px-5">
        <div className="grid grid-cols-2 gap-3">
          {enabled.has("calendar") && (
            <Link to="/calendar" className="hero-glow card-interactive col-span-2 block rounded-3xl p-5 text-cream-50 shadow-lg">
              <div className="flex items-center justify-between">
                <h2 className="text-xs font-semibold uppercase tracking-wide text-plum-100/80">Nächste Termine</h2>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5 text-plum-100/60">
                  <path d="m9 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              {todayEvents.length === 0 && tomorrowEvents.length === 0 && laterEvents.length === 0 ? (
                <p className="mt-3 text-sm text-plum-100/70">Keine anstehenden Termine</p>
              ) : (
                <div className="mt-3 space-y-3">
                  {todayEvents.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-plum-100/60">Heute</p>
                      <ul className="mt-1.5 space-y-1.5">
                        {todayEvents.map((ev) => (
                          <li key={ev.id} className="flex items-center gap-2.5 text-sm">
                            {ev.categoryId && categoryById.get(ev.categoryId) && (
                              <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: categoryById.get(ev.categoryId)!.color }} />
                            )}
                            <span className="truncate text-cream-50">{ev.title}</span>
                            {ev.time && <span className="ml-auto flex-shrink-0 text-xs text-plum-100/70">{ev.time}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {tomorrowEvents.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-plum-100/60">Morgen</p>
                      <ul className="mt-1.5 space-y-1.5">
                        {tomorrowEvents.map(({ ev }) => (
                          <li key={ev.id} className="flex items-center gap-2.5 text-sm">
                            {ev.categoryId && categoryById.get(ev.categoryId) && (
                              <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: categoryById.get(ev.categoryId)!.color }} />
                            )}
                            <span className="truncate text-cream-50">{ev.title}</span>
                            {ev.time && <span className="ml-auto flex-shrink-0 text-xs text-plum-100/70">{ev.time}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {laterEvents.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-plum-100/60">Demnächst</p>
                      <ul className="mt-1.5 space-y-1.5">
                        {laterEvents.map(({ ev, next }) => (
                          <li key={ev.id} className="flex items-center gap-2.5 text-sm">
                            <span className="flex-shrink-0 rounded-full bg-white/15 px-2 py-0.5 text-xs font-semibold text-cream-50">
                              {new Date(next + "T00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}
                            </span>
                            {ev.categoryId && categoryById.get(ev.categoryId) && (
                              <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: categoryById.get(ev.categoryId)!.color }} />
                            )}
                            <span className="truncate text-cream-50">{ev.title}</span>
                            {ev.time && <span className="ml-auto flex-shrink-0 text-xs text-plum-100/70">{ev.time}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </Link>
          )}

          {enabled.has("todos") && (
            <CompactCard
              to="/todos"
              iconPath="M4 4h16v16H4zM8 12.5l3 3 5-6.5"
              tint="plum"
              title="Todos"
              value={openTodos.length > 0 ? String(openTodos.length) : undefined}
              sub={openTodos.length === 0 ? "Alles erledigt" : openTodos[0].text}
            />
          )}

          {enabled.has("savings") && (
            <CompactCard
              to="/savings"
              iconPath="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"
              tint="gold"
              title="Sparziele"
              value={topGoalPct !== null ? `${topGoalPct}%` : undefined}
              sub={topGoal ? topGoal.title : "Noch keine Sparziele"}
            />
          )}

          {enabled.has("shopping") && (
            <CompactCard
              to="/lists"
              iconPath="M9 6h11M9 12h11M9 18h11M4.5 6h.01M4.5 12h.01M4.5 18h.01"
              tint="plum"
              title="Listen"
              value={lists.length > 0 ? String(lists.length) : undefined}
              sub={lists.length === 0 ? "Noch keine Liste" : lists.map((l) => l.name).join(", ")}
            />
          )}

          {enabled.has("links") && (
            <CompactCard
              to="/links"
              iconPath="M9 15l6-6M10 6l.7-.7a3.5 3.5 0 0 1 5 5L15 11M14 18l-.7.7a3.5 3.5 0 0 1-5-5l.7-.7"
              tint="gold"
              title="Links"
              value={linkBoards.length > 0 ? String(linkBoards.length) : undefined}
              sub={linkBoards.length === 0 ? "Noch keine Sammlung" : linkBoards.map((b) => b.name).join(", ")}
            />
          )}

          {enabled.has("dates") &&
            (pinnedDates.length > 0 ? (
              <div className="col-span-2">
                <div className="flex items-center justify-between px-0.5">
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-plum-600 dark:text-night-accent">Wichtige Daten</h2>
                  <Link to="/dates" aria-label="Alle wichtigen Daten" className="text-ink-400 dark:text-night-faint">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5">
                      <path d="m9 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </Link>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {pinnedDates.map((d) => {
                    const color = d.categoryId ? (dateCategoryById.get(d.categoryId)?.color ?? "#a6949b") : "#a6949b";
                    return (
                      <Link
                        key={d.id}
                        to="/dates"
                        className="card-interactive flex flex-col justify-between rounded-2xl p-3 shadow-sm"
                        style={{ backgroundColor: color, color: "#fdf6f3" }}
                      >
                        <p className="truncate text-sm font-semibold leading-snug">{d.title}</p>
                        <p className="mt-2 text-xs font-medium opacity-90">{dateBadge(d)}</p>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ) : (
              <CompactCard
                to="/dates"
                iconPath="M4 5h16v16H4zM4 9h16M8 3v4M16 3v4M12 13v5M9.5 15.5h5"
                tint="plum"
                title="Wichtige Daten"
                value={nextDate ? nextOccurrence(nextDate.date, nextDate.repeatsYearly).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }) : undefined}
                sub={nextDate ? nextDate.title : "Noch keine Daten"}
              />
            ))}

          {enabled.has("notes") && (
            <Link to="/notes" className="card card-interactive col-span-2 block rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xs font-semibold uppercase tracking-wide text-plum-600 dark:text-night-accent">Letzte Notiz</h2>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5 text-ink-400 dark:text-night-faint">
                  <path d="m9 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              {notes.length === 0 ? (
                <p className="mt-2 text-sm text-ink-400 dark:text-night-faint">Noch keine Notizen</p>
              ) : (
                <p className="mt-2 line-clamp-2 text-sm text-ink-900 dark:text-night-text">{notes[0].text}</p>
              )}
            </Link>
          )}
        </div>
      </div>

      <Sheet open={customizeOpen} onClose={() => setCustomizeOpen(false)} title="Startseite anpassen">
        <p className="text-sm text-ink-400 dark:text-night-faint">Wähle, welche Bereiche auf eurer Startseite erscheinen sollen.</p>
        <ul className="mt-3 divide-y divide-cream-100 dark:divide-night-border">
          {WIDGETS.map((w) => (
            <li key={w.id} className="flex items-center justify-between py-3">
              <span className="text-sm text-ink-900 dark:text-night-text">{w.label}</span>
              <button
                onClick={() => toggle(w.id)}
                aria-pressed={enabled.has(w.id)}
                className={`relative h-7 w-12 flex-shrink-0 rounded-full transition-colors ${enabled.has(w.id) ? "bg-plum-700" : "bg-cream-200 dark:bg-night-border"}`}
              >
                <span
                  className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-[left] ${
                    enabled.has(w.id) ? "left-6" : "left-1"
                  }`}
                />
              </button>
            </li>
          ))}
        </ul>

        <p className="mt-6 text-sm text-ink-400 dark:text-night-faint">
          Wähle {MIN_NAV_ITEMS}–{MAX_NAV_ITEMS} Seiten für die Navigationsleiste unten ({navIds.length}/{MAX_NAV_ITEMS}
          ausgewählt).
        </p>
        <ul className="mt-3 divide-y divide-cream-100 dark:divide-night-border">
          {ALL_NAV_ITEMS.map((item) => {
            const checked = navIds.includes(item.id);
            const disabled = checked ? navIds.length <= MIN_NAV_ITEMS : navIds.length >= MAX_NAV_ITEMS;
            return (
              <li key={item.id} className="flex items-center justify-between py-3">
                <span className={`text-sm ${disabled && !checked ? "text-ink-300 dark:text-night-faint/50" : "text-ink-900 dark:text-night-text"}`}>
                  {item.label}
                </span>
                <button
                  onClick={() => toggleNavItem(item.id)}
                  disabled={disabled}
                  aria-pressed={checked}
                  className={`relative h-7 w-12 flex-shrink-0 rounded-full transition-colors disabled:opacity-40 ${checked ? "bg-plum-700" : "bg-cream-200 dark:bg-night-border"}`}
                >
                  <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-[left] ${checked ? "left-6" : "left-1"}`} />
                </button>
              </li>
            );
          })}
        </ul>
      </Sheet>
    </main>
  );
}

const TINTS = {
  plum: { bg: "bg-plum-100 dark:bg-night-surface-raised", fg: "text-plum-700 dark:text-night-accent" },
  gold: { bg: "bg-gold-100 dark:bg-night-surface-raised", fg: "text-gold-500" },
};

function CompactCard({
  to,
  iconPath,
  tint,
  title,
  value,
  sub,
}: {
  to: string;
  iconPath: string;
  tint: keyof typeof TINTS;
  title: string;
  value?: string;
  sub: string;
}) {
  const t = TINTS[tint];
  return (
    <Link to={to} className="card card-interactive flex flex-col rounded-2xl p-4">
      <div className="flex items-center justify-between">
        <span className={`flex h-9 w-9 items-center justify-center rounded-full ${t.bg} ${t.fg}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-4.5 w-4.5">
            <path d={iconPath} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        {value && <span className="font-display text-lg font-semibold text-ink-900 dark:text-night-text">{value}</span>}
      </div>
      <p className="mt-3 text-sm font-semibold text-ink-900 dark:text-night-text">{title}</p>
      <p className="mt-0.5 truncate text-xs text-ink-400 dark:text-night-faint">{sub}</p>
    </Link>
  );
}

function MoodHeroChip({ label, mood }: { label: string; mood: Mood | null }) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2.5">
      <span className="flex-shrink-0 text-3xl leading-none">{mood?.emoji ?? "＋"}</span>
      <div className="min-w-0">
        <p className="text-xs font-medium text-cream-50">{label}</p>
        {mood ? (
          <p className="truncate text-[11px] text-plum-100/70">{mood.note || mood.label}</p>
        ) : (
          <p className="truncate text-[11px] text-plum-100/50">Noch offen</p>
        )}
      </div>
    </div>
  );
}
